import {
  forwardRef,
  type Ref,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import {
  useTransition,
  animated,
  config,
  type SpringValue,
} from '@react-spring/web';
import { useGesture } from '@use-gesture/react';
import clsx from 'clsx';
import { useMeasure } from 'react-use';
import { X } from '@hungryroot/icons';
import {
  Heading,
  IconButton,
  ModalContent,
  ModalOverlay,
  useModalSheetInputIosKeyboard,
} from '@hungryroot/ramen';
import styles from './DrawerModal.module.css';

const AnimatedDialogOverlay = animated(ModalOverlay);
const AnimatedDialogContent = animated(ModalContent);

export interface Props {
  active: boolean;
  className?: string;
  testid?: string;
  children: ReactNode;
  ariaLabel: string;
  initialFocusRef?: RefObject<HTMLElement>;
  onRequestDismiss: () => void;
  heading?: string;
}

export function DrawerModal(props: Props) {
  const {
    active,
    ariaLabel,
    onRequestDismiss,
    className,
    initialFocusRef,
    testid,
    heading,
    children,
  } = props;
  const [ref, { height }] = useMeasure<HTMLDivElement>();
  const [dragY, setDragY] = useState(0);

  const dragPct = height === 0 ? 0 : dragY / height;
  const transitions = useTransition(active, {
    config: config.stiff,
    from: {
      transform: `translate3d(0, 100%, 0)`,
      opacity: 0,
    },
    update: {
      transform: `translate3d(0, ${dragPct * 100}%, 0)`,
      opacity: 1,
    },
    enter: {
      transform: `translate3d(0, 0%, 0)`,
      opacity: 1,
    },
    leave: {
      transform: `translate3d(0, 100%, 0)`,
      opacity: 0,
    },
  });

  const drag = useGesture({
    onDrag(state) {
      setDragY(state.xy[1] - state.initial[1]);
    },
    onDragEnd(state) {
      const { swipe } = state;
      if (swipe[1] === 1) {
        onRequestDismiss();
      }
      setDragY(0);
    },
  });

  return transitions(({ transform, opacity }, item) => {
    if (item === false) {
      return null;
    }

    const spring = { transform };

    return (
      <AnimatedDialog
        onRequestDismiss={onRequestDismiss}
        testid={testid}
        opacity={opacity}
        item={item}
        initialFocusRef={initialFocusRef}
        ariaLabel={ariaLabel}
        spring={spring}
        className={className}
        heading={heading}
        drag={drag}
        ref={ref}
        children={children}
      />
    );
  });
}

type AnimatedDialogProps = {
  onRequestDismiss: () => void;
  testid?: string;
  opacity: SpringValue<number>;
  item: boolean;
  initialFocusRef?: RefObject<HTMLElement>;
  ariaLabel: string;
  spring: { transform: SpringValue<string> };
  className?: string;
  heading?: string;
  drag: ReturnType<typeof useGesture>;
  children: ReactNode;
};

const AnimatedDialog = forwardRef(
  (props: AnimatedDialogProps, ref: Ref<HTMLDivElement>) => {
    const {
      onRequestDismiss,
      testid,
      opacity,
      item,
      initialFocusRef,
      ariaLabel,
      spring,
      className,
      heading,
      drag,
      children,
    } = props;

    const { overlayRef } = useModalSheetInputIosKeyboard();

    return (
      <AnimatedDialogOverlay
        ref={(element) => {
          overlayRef.current = element;
        }}
        onDismiss={onRequestDismiss}
        data-testid={testid}
        style={{ opacity }}
        className={styles.container}
        isOpen={item}
        initialFocusRef={initialFocusRef}
      >
        <AnimatedDialogContent
          ariaLabel={ariaLabel}
          data-testid={`${testid}-body`}
          style={spring}
          ref={ref}
          className={styles['content-wrapper']}
        >
          <div className={clsx(styles.body, className)}>
            <span
              data-testid={`${testid}-touch-bar`}
              className={styles['touch-bar']}
              {...drag()}
            ></span>
            {heading ? (
              <div className={styles.header}>
                <Heading variant="H4">{heading}</Heading>

                <IconButton
                  className={styles['close-icon-button']}
                  style="muted"
                  prominence="tertiary"
                  size="medium"
                  icon={<X />}
                  onClick={onRequestDismiss}
                  ariaLabel="Dismiss modal"
                />
              </div>
            ) : (
              <span
                onClick={onRequestDismiss}
                data-testid={`${testid}-close-button`}
                className={styles['close-button']}
              >
                <X />
              </span>
            )}
            {children}
          </div>
        </AnimatedDialogContent>
      </AnimatedDialogOverlay>
    );
  }
);
